import { z } from 'zod';

import { apiRequest, apiRequestBlob } from '@/api/client';
import { pageSchema } from '@/api/types';

/** Schemas mirroring `app/api/v1/subscriptions.py`. */

/**
 * One stored feature value: a quota (number, or null for unlimited), a switch
 * (boolean), or `'ADDON'` - a switch that is available but billed separately.
 */
export const featureValueSchema = z.union([
  z.number(),
  z.boolean(),
  z.literal('ADDON'),
  z.null(),
]);
export type FeatureValue = z.infer<typeof featureValueSchema>;

/** `{ FEATURE_KEY: value }` - one plan's complete entitlement table. */
export const featureTableSchema = z.record(featureValueSchema);
export type FeatureTable = z.infer<typeof featureTableSchema>;

export const FEATURE_KINDS = ['QUOTA', 'SWITCH'] as const;
export type FeatureKind = (typeof FEATURE_KINDS)[number];

/**
 * What a feature *is*, from the backend catalogue. Both the platform panel and
 * the merchant billing page read this rather than carrying their own list, so
 * a feature added on the server appears on both screens with nothing to deploy
 * here.
 */
export const featureSpecSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string(),
  category: z.string(),
  kind: z.enum(FEATURE_KINDS),
  unit: z.string().nullable(),
  /**
   * A quota stored in one unit and read in another. Setup-assistance time is
   * stored in minutes so that seven and a half hours can be expressed at all,
   * and shown in hours because nobody configures a plan in minutes. Absent on
   * every other quota, where the two are the same thing.
   */
  display_unit: z.string().nullable().default(null),
  display_scale: z.number().int().positive().default(1),
  per_month: z.boolean(),
  /** Whether the API refuses requests over this feature, or it is display-only. */
  enforced: z.boolean(),
  counted: z.boolean(),
  addon_capable: z.boolean(),
  /**
   * Where the platform owner put this feature, low first. The array already
   * arrives in this order - it is here so a screen that regroups the features
   * can sort them back, and so the panel can show the position it is editing.
   * Optional for the same reason every field added later is: a cached response
   * from before it existed must still parse.
   */
  sort_order: z.number().optional().default(0),
  /** `ACTIVE` everywhere merchants can see. The panel also sees the rest. */
  status: z.string().optional().default('ACTIVE'),
  /** True when the shipped code knows this feature by name. */
  is_builtin: z.boolean().optional().default(true),
});
export type FeatureSpec = z.infer<typeof featureSpecSchema>;

export const featureCategorySchema = z.object({ key: z.string(), label: z.string() });
export type FeatureCategory = z.infer<typeof featureCategorySchema>;

export const featureCatalogueSchema = z.object({
  categories: z.array(featureCategorySchema),
  features: z.array(featureSpecSchema),
});
export type FeatureCatalogue = z.infer<typeof featureCatalogueSchema>;

/** A plan as the merchant sees it: terms plus its complete feature table. */
export const cataloguePlanSchema = z.object({
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  price_minor: z.number(),
  currency: z.string(),
  billing_interval: z.string(),
  billing_interval_count: z.number().optional().default(1),
  /**
   * The free trial a new subscriber gets, in days. Already zero when the
   * platform owner has the plan's trial switched off, whatever length is
   * configured behind the switch - the server answers for the switch.
   */
  trial_days: z.number(),
  trial_enabled: z.boolean().optional().default(false),
  sort_order: z.number(),
  /**
   * Sales-led (Enterprise): shown with "Call Sales" in place of a price and a
   * talk-to-sales button. Never checked out on - staff assign it by hand.
   */
  contact_sales: z.boolean().optional().default(false),
  /** True when choosing this plan sets up an auto-renewing Razorpay mandate. */
  recurring: z.boolean().optional().default(false),
  grace_period_days: z.number().optional(),
  /**
   * The optional one-time "Store setup by NeonCarts team" add-on on this plan,
   * or null where it is not offered. Its terms are the service's, not limits.
   */
  setup: z
    .object({ fee_minor: z.number(), products: z.number(), images_per_product: z.number() })
    .nullable()
    .optional()
    .default(null),
  features: featureTableSchema,
  /**
   * `{ FEATURE_KEY: position }`, sparse: where this plan wants a feature
   * shown, when that differs from the catalogue's own order. Empty - which is
   * every plan until the owner drags something - means "as the catalogue has
   * it", which is how every plan was displayed before this existed.
   */
  feature_order: z.record(z.number()).optional().default({}),
});
export type CataloguePlan = z.infer<typeof cataloguePlanSchema>;

export const merchantCatalogueSchema = featureCatalogueSchema.extend({
  plans: z.array(cataloguePlanSchema),
  /**
   * Whether the platform owner offers new merchants a free trial of the
   * dashboard before they choose a plan. Whether *this* merchant may start one
   * is `panel_trial.available` on the subscription summary.
   */
  signup_skip_allowed: z.boolean().optional().default(true),
  /** How many days that trial lasts. */
  panel_trial_days: z.number().optional().default(7),
  /**
   * The cadence the pricing page and the subscribe chooser open on - the
   * owner's call, made on the panel's plans screen.
   *
   * Nullable rather than defaulted, because "no answer" and "monthly" are
   * different things: a payload from a build that predates the setting must
   * leave the screens leading with the cheapest cycle on sale, which is what
   * they did before it existed, rather than assert a cadence nobody chose. A
   * cadence with no plans on it is ignored by the screens for the same reason.
   */
  default_billing_interval: z.string().nullish(),
  /**
   * The GST percentage added on top of every plan price, as a decimal string
   * (`"18.00"`); `"0"` while the platform charges none. The screens say
   * "+ GST" beside a price rather than adding it themselves - the figure to
   * pay comes from the server's quote (`fetchChargeQuote`).
   */
  gst_rate: z.string().optional().default('0'),
});
export type MerchantCatalogue = z.infer<typeof merchantCatalogueSchema>;

/** Whether plan prices on this catalogue have GST added on top. */
export function gstApplies(catalogue: { gst_rate?: string } | null | undefined): boolean {
  return Number(catalogue?.gst_rate ?? 0) > 0;
}

/** A counted feature reports usage; an on/off feature reports a flag. */
export const featureUsageSchema = z.union([
  z.object({
    used: z.number(),
    limit: z.number().nullable(),
    remaining: z.number().nullable(),
  }),
  z.object({ enabled: z.boolean(), addon: z.boolean().optional() }),
]);

/** Why a merchant's billing was locked - the same enum as `subscriptions.lapse_reason`. */
export const BILLING_LOCK_REASONS = [
  'PERIOD_ENDED',
  'GRACE_EXPIRED',
  'TRIAL_ENDED',
  'PANEL_TRIAL_OVER',
] as const;
export type BillingLockReason = (typeof BILLING_LOCK_REASONS)[number];

/**
 * The lock on a merchant whose plan lapsed and was never renewed: the store
 * is closed to shoppers and the dashboard is limited to billing until a plan
 * is paid for. Carried by `/auth/me` and by the subscription summary.
 *
 * Every field but the reason is optional, so a lock from an older backend
 * still parses - the guard needs only to know that one is set.
 */
export const billingLockSchema = z.object({
  reason: z.string(),
  locked_at: z.string().nullable().optional(),
  plan_code: z.string().nullable().optional(),
  plan_name: z.string().nullable().optional(),
  /** The GST-inclusive renewal of the lapsed plan, or null when there is none to quote. */
  amount_due: z.string().nullable().optional(),
  grace_ended_at: z.string().nullable().optional(),
});
export type BillingLock = z.infer<typeof billingLockSchema>;

export const RECURRING_STATES = [
  'MANUAL',
  'PENDING',
  'TRIALING',
  'ACTIVE',
  'PAST_DUE',
  'CANCEL_SCHEDULED',
  'CANCELLED',
  'PAUSED',
  'COMPLETED',
  'EXPIRED',
] as const;
export type RecurringState = (typeof RECURRING_STATES)[number];

/**
 * The free trial on a mandate, when it has one.
 *
 * Every date is the server's: the trial's bounds, the first payment (the
 * trial end, which is the Razorpay subscription's `start_at`) and the renewal
 * that follows it. `status` is `PENDING` until the mandate is authorised,
 * `ACTIVE` during the trial, `ENDED` once Razorpay has started charging and
 * `CANCELLED` when it was stopped before then.
 */
export const recurringTrialSchema = z.object({
  status: z.enum(['PENDING', 'ACTIVE', 'ENDED', 'CANCELLED']),
  days: z.number(),
  started_at: z.string().nullable(),
  ends_at: z.string().nullable(),
  first_payment_at: z.string().nullable(),
  /** What Razorpay collects when the trial ends, GST included. */
  first_payment_amount: z.string().nullable(),
  next_billing_after_first_at: z.string().nullable(),
});
export type RecurringTrial = z.infer<typeof recurringTrialSchema>;

/**
 * The merchant's auto-renewing Razorpay mandate, mirrored from Razorpay's own
 * subscription by its webhooks. Null for a merchant who has only ever paid by
 * hand. `state` is what NeonCarts makes of it for access; `provider_status`
 * is Razorpay's own word, shown for support.
 */
export const recurringSubscriptionSchema = z.object({
  state: z.string(),
  provider_status: z.string().nullable(),
  razorpay_subscription_id: z.string(),
  razorpay_plan_id: z.string().nullable(),
  /** The plan price each cycle, before GST. */
  amount: z.string().nullable(),
  /** The GST percentage inside the mandate's Razorpay plan. */
  tax_rate: z.string().optional().default('0'),
  /** What Razorpay actually takes each cycle: `amount` plus its GST. */
  amount_with_tax: z.string().nullable().optional(),
  currency: z.string().nullable(),
  quantity: z.number(),
  billing_interval: z.string().nullable(),
  billing_interval_count: z.number().nullable(),
  current_period_start: z.string().nullable(),
  current_period_end: z.string().nullable(),
  next_billing_at: z.string().nullable(),
  started_at: z.string().nullable(),
  cancelled_at: z.string().nullable(),
  cancel_at_period_end: z.boolean(),
  ended_at: z.string().nullable(),
  grace_period_ends_at: z.string().nullable(),
  last_payment_error: z.string().nullable(),
  paid_cycles: z.number(),
  auto_renew: z.boolean(),
  scheduled_plan: z.object({ code: z.string(), name: z.string() }).nullable(),
  authorization_expires_at: z.string().nullable().optional(),
  last_synced_at: z.string().nullable().optional(),
  trial: recurringTrialSchema.nullable().optional(),
  /**
   * A plan change on this mandate that is waiting for Razorpay Checkout: the
   * new mandate exists, the first period is priced, and nothing moves until
   * the merchant authorises it or the offer expires.
   */
  pending_change: z
    .object({
      plan_code: z.string(),
      plan_name: z.string(),
      invoice_id: z.string(),
      amount: z.string().nullable().optional(),
      expires_at: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});
export type RecurringSubscription = z.infer<typeof recurringSubscriptionSchema>;

/** One plan's terms for the optional "Store setup by NeonCarts team" add-on. */
export const setupPlanOfferSchema = z.object({
  plan: z.string(),
  available: z.boolean(),
  /** The fee in paise; the add-on is hidden on a plan where it is 0. */
  amount_minor: z.number(),
  currency: z.string().optional().default('INR'),
  /** The service's promise - not a plan limit. */
  products: z.number(),
  images_per_product: z.number(),
  hours: z.number().optional(),
  /** The plan's own product limit; null is unlimited. */
  plan_product_limit: z.number().nullable(),
  /** What the team will actually set up: the promise, capped by the plan. */
  products_in_scope: z.number(),
});
export type SetupPlanOffer = z.infer<typeof setupPlanOfferSchema>;

/**
 * Whether this merchant may still buy the setup add-on. Only new paid signups
 * are offered it; `plans` is empty once the first paid checkout has completed.
 */
export const setupOfferSchema = z.object({
  eligible: z.boolean(),
  available: z.boolean(),
  plans: z.array(setupPlanOfferSchema).optional().default([]),
  /**
   * For a merchant already on a paid plan who skipped the add-on at checkout:
   * their own plan's terms, to buy it on its own. Null otherwise - including
   * once it has been bought.
   */
  later: setupPlanOfferSchema.nullable().optional().default(null),
});
export type SetupOffer = z.infer<typeof setupOfferSchema>;

export const setupAddonPurchaseSchema = z.object({
  invoice_id: z.string(),
  /** The setup fee plus its GST - what Checkout takes. */
  amount: z.string(),
  setup_fee_amount: z.string().optional(),
  tax_amount: z.string().optional(),
  currency: z.string().optional().default('INR'),
  payment: z.record(z.unknown()),
});

/**
 * Buy the setup add-on on its own: a one-off Razorpay payment for the fee
 * alone. The setup job is raised when the signed webhook confirms the money.
 */
export function buySetupAddon() {
  return apiRequest('/subscription/setup-addon', setupAddonPurchaseSchema, { method: 'POST' });
}

/** The setup job a paid add-on raised, for the billing page. */
export const setupPurchaseSchema = z.object({
  request_id: z.string(),
  status: z.string(),
  scope_products: z.number().nullable().optional(),
  scope_images_per_product: z.number().nullable().optional(),
  created_at: z.string().nullable().optional(),
  completed_at: z.string().nullable().optional(),
});
export type SetupPurchase = z.infer<typeof setupPurchaseSchema>;

/**
 * The free dashboard trial a new merchant can start instead of choosing a
 * plan, when the platform owner offers one.
 *
 * `available` - the subscribe screen may offer to start it now; `days` - how
 * long it would last; `active` - running with no plan chosen yet (the sidebar
 * counts down); `expired` - ran out with no plan, so only a paid plan reopens
 * the store.
 */
export const panelTrialSchema = z.object({
  available: z.boolean(),
  days: z.number(),
  started_at: z.string().nullable().optional(),
  ends_at: z.string().nullable().optional(),
  active: z.boolean(),
  expired: z.boolean(),
});
export type PanelTrial = z.infer<typeof panelTrialSchema>;

export const subscriptionSchema = z.object({
  plan: z.string(),
  plan_name: z.string().optional(),
  plan_description: z.string().nullable().optional(),
  price_minor: z.number().optional(),
  currency: z.string().optional(),
  billing_interval: z.string().optional(),
  billing_interval_count: z.number().optional(),
  trial_days: z.number().optional(),
  trial_enabled: z.boolean().optional(),
  active: z.boolean().optional(),
  current_period_end: z.string().nullable().optional(),
  trial_ends_at: z.string().nullable().optional(),
  cancelled_at: z.string().nullable().optional(),
  overridden_features: z.array(z.string()).optional(),
  features: z.record(featureUsageSchema),
  recurring: recurringSubscriptionSchema.nullable().optional(),
  setup_offer: setupOfferSchema.nullable().optional(),
  setup_purchase: setupPurchaseSchema.nullable().optional(),
  /**
   * A hand-paid plan whose period has ended: the days left to pay before the
   * store closes, and why the clock is running.
   */
  grace: z
    .object({ ends_at: z.string().nullable().optional(), reason: z.string().nullable().optional() })
    .nullable()
    .optional(),
  billing_lock: billingLockSchema.nullable().optional(),
  /** Absent from a backend that predates the trial: no trial, nothing offered. */
  panel_trial: panelTrialSchema.nullable().optional(),
  /**
   * False once the free trial has run out unpaid: the subscribe screen hides
   * Free, and the server refuses it. Defaults to true for an older backend.
   */
  free_plan_allowed: z.boolean().optional().default(true),
});

/** `{ PLAN: { FEATURE: limit } }` - limit is a count, a boolean, or null for unlimited. */
export const plansSchema = z.record(featureTableSchema);

export type Subscription = z.infer<typeof subscriptionSchema>;
export type FeatureUsage = z.infer<typeof featureUsageSchema>;
export type QuotaUsage = Extract<FeatureUsage, { used: number }>;
export type SwitchUsage = Extract<FeatureUsage, { enabled: boolean }>;
export type Plans = z.infer<typeof plansSchema>;

export function isQuotaUsage(usage: FeatureUsage): usage is QuotaUsage {
  return 'used' in usage;
}

export function fetchSubscription() {
  return apiRequest('/subscription', subscriptionSchema);
}

/** Start this store's one free trial of the dashboard. */
export function startPanelTrial() {
  return apiRequest('/subscription/panel-trial', subscriptionSchema, { method: 'POST' });
}

export function fetchPlans() {
  return apiRequest('/subscription/plans', plansSchema);
}

/** `{ FEATURE_KEY: granted }` for any signed-in member - what the sidebar hides by. */
export function fetchEntitlements() {
  return apiRequest('/subscription/entitlements', z.record(z.boolean()));
}

/** The feature catalogue and every plan on sale - the same rows the platform panel edits. */
export function fetchCatalogue() {
  return apiRequest('/subscription/catalogue', merchantCatalogueSchema);
}

/**
 * The same catalogue, asked for without a session - what the public pricing
 * page reads.
 *
 * The endpoint is unauthenticated because the plans on sale are public
 * information. Naming the realm anyway keeps a signed-in merchant's bearer
 * token off a marketing page, and stops an expired one from setting off a
 * token refresh there.
 */
/**
 * Plans made for this merchant alone - a negotiated Enterprise price - in the
 * catalogue's shape. Never on the public catalogue; the payment link
 * `/subscribe?plan=CODE` is how a merchant reaches one.
 */
export function fetchOffers() {
  return apiRequest('/subscription/offers', z.array(cataloguePlanSchema));
}

/** One plan, as the read-only page its share link opens reads it. */
export const sharedPlanSchema = featureCatalogueSchema.extend({
  plan: cataloguePlanSchema,
  /** The business a reserved plan was made for, for "Prepared for …". */
  prepared_for: z.string().nullable().optional().default(null),
  reserved: z.boolean().optional().default(false),
  /** Whether the plan's payment link would let the merchant pay today. */
  checkout: z.boolean().optional().default(false),
  gst_rate: z.string().optional().default('0'),
});
export type SharedPlan = z.infer<typeof sharedPlanSchema>;

/**
 * A plan's terms and feature table through its share link. No session: the
 * token in the link is the credential, and whoever was sent it may not have
 * an account yet.
 */
export function fetchSharedPlan(planId: string, token: string) {
  return apiRequest(
    `/subscription/shared-plans/${encodeURIComponent(planId)}/${encodeURIComponent(token)}`,
    sharedPlanSchema,
    { auth: 'none' },
  );
}

export function fetchPublicCatalogue() {
  return apiRequest('/subscription/catalogue', merchantCatalogueSchema, { auth: 'none' });
}

/**
 * The result of asking to change plan.
 *
 * A free plan applies at once and comes back with `payment: null`. A paid one
 * comes back with a gateway payload to complete - and the plan in
 * `subscription` is still the *old* one, because the change lands only when the
 * signed webhook confirms the money.
 */
export const PLAN_CHANGE_MODES = [
  'applied',
  'payment',
  'subscription',
  'changed',
  'scheduled',
  'cancel_scheduled',
] as const;

export const planChangeSchema = z.object({
  /**
   * Which flow the change took: `applied` (done), `payment` (open Checkout
   * for a one-off order), `subscription` (open Checkout to authorise the
   * mandate), `changed` / `scheduled` (the mandate moved now / at cycle end),
   * `cancel_scheduled` (moving to Free ends the mandate at cycle end).
   */
  mode: z.enum(PLAN_CHANGE_MODES).optional().default('payment'),
  subscription: subscriptionSchema,
  invoice_id: z.string().nullable(),
  amount: z.string().nullable(),
  payment: z.record(z.unknown()).nullable(),
  recurring: z.record(z.unknown()).nullable().optional(),
  effective_at: z.string().nullable().optional(),
  /**
   * The referral discount on this charge. `amount` is already net of it -
   * these are here so the screen can show what came off without recomputing
   * it from the catalogue and arriving at a figure the invoice disagrees with.
   */
  subtotal_amount: z.string().nullable().optional(),
  discount_amount: z.string().nullable().optional(),
  referral_code: z.string().nullable().optional(),
  /** The one-time setup add-on on this charge, already inside `amount`. */
  setup_fee_amount: z.string().nullable().optional(),
  /**
   * The GST added on top, already inside `amount`, and its split: CGST +
   * SGST within the platform's state, IGST otherwise.
   */
  tax_rate: z.string().nullable().optional(),
  tax_amount: z.string().nullable().optional(),
  cgst_amount: z.string().nullable().optional(),
  sgst_amount: z.string().nullable().optional(),
  igst_amount: z.string().nullable().optional(),
  place_of_supply: z.string().nullable().optional(),
  /**
   * `MANDATE` when this payment buys one period and auto-renewal still has to
   * be authorised afterwards - a referred merchant taking their discounted
   * first cycle on an auto-renewing plan. `follow_up_at` is when the mandate
   * would start charging, which is the end of the period being paid for now.
   */
  follow_up: z.literal('MANDATE').nullable().optional(),
  follow_up_at: z.string().nullable().optional(),
  /**
   * The credit for the unused part of the period being left, already inside
   * `amount`: what came off, and the days it was worked out from.
   */
  credit_amount: z.string().nullable().optional(),
  credit_days: z.number().nullable().optional(),
  credit_period_days: z.number().nullable().optional(),
});

export type PlanChange = z.infer<typeof planChangeSchema>;

/**
 * Ask to move onto `plan`.
 *
 * `forgoTrial` is the merchant's answer on a plan that has both a free trial
 * and a referral discount waiting: the two cannot be combined, and which is
 * worth more depends on the plan. It carries no money - the server still
 * prices the discount from the benefit it granted - and setting it where no
 * discount applies simply keeps the trial.
 *
 * `includeSetup` asks for the one-time setup add-on on this first payment.
 * The server re-checks eligibility and ignores it for a merchant who is not
 * a new paid signup.
 */
export function changePlan(
  plan: string,
  { forgoTrial = false, includeSetup = false }: { forgoTrial?: boolean; includeSetup?: boolean } = {},
) {
  return apiRequest('/subscription', planChangeSchema, {
    method: 'PUT',
    body: {
      plan,
      ...(forgoTrial ? { forgo_trial: true } : {}),
      ...(includeSetup ? { include_setup: true } : {}),
    },
  });
}

/**
 * What a plan change would charge today - plan, discount, setup, GST and the
 * total payable - priced by the server exactly as `changePlan` will charge it.
 * Raises nothing; the payment screen reads its summary from here so the lines
 * on screen are the charge, not this browser's arithmetic.
 */
export const chargeQuoteSchema = z.object({
  plan_code: z.string(),
  currency: z.string(),
  tax_enabled: z.boolean(),
  list_amount: z.string(),
  discount_amount: z.string(),
  /** The plan's part of today's charge: zero on a trial or a paid period. */
  plan_amount: z.string(),
  setup_fee_amount: z.string(),
  taxable_amount: z.string(),
  tax_rate: z.string(),
  tax_amount: z.string(),
  cgst_amount: z.string(),
  sgst_amount: z.string(),
  igst_amount: z.string(),
  total_amount: z.string(),
  place_of_supply: z.string().nullable(),
  intra_state: z.boolean(),
  /** Each later cycle, GST included. */
  renewal_amount: z.string(),
  renewal_tax_amount: z.string(),
  buyer_gstin: z.string().nullable(),
  buyer_state: z.string().nullable(),
  /** Whether the download after paying will be a tax invoice. */
  platform_gst_registered: z.boolean(),
  /**
   * How the change would land: `immediate` (paid and applied now), `deferred`
   * (at the end of the current period, nothing to pay today), `renewal` (the
   * same plan again, for the period after this one) or `same` (the plan the
   * merchant is already on). Defaulted so a quote from an older backend reads
   * as the plain charge it always was.
   */
  mode: z.enum(['immediate', 'deferred', 'renewal', 'same']).optional().default('immediate'),
  deferred_reason: z.string().nullable().optional(),
  /** The credit for the unused part of the current period, before and with its GST. */
  credit_amount: z.string().optional().default('0'),
  credit_tax_amount: z.string().optional().default('0'),
  credit_total_amount: z.string().optional().default('0'),
  credit_days: z.number().nullable().optional(),
  credit_period_days: z.number().nullable().optional(),
  credit_source_plan_name: z.string().nullable().optional(),
  /** The period this charge buys; for a renewal it starts when the current one ends. */
  period_start: z.string().nullable().optional(),
  period_end: z.string().nullable().optional(),
  /** When the plan actually changes: now, or the current period's end when deferred. */
  effective_at: z.string().nullable().optional(),
});
export type ChargeQuote = z.infer<typeof chargeQuoteSchema>;

export function fetchChargeQuote(
  plan: string,
  { forgoTrial = false, includeSetup = false }: { forgoTrial?: boolean; includeSetup?: boolean } = {},
) {
  const params = new URLSearchParams({ plan });
  if (forgoTrial) params.set('forgo_trial', 'true');
  if (includeSetup) params.set('include_setup', 'true');
  return apiRequest(`/subscription/quote?${params}`, chargeQuoteSchema);
}

/**
 * Tell the server Checkout finished authorising the mandate. The server
 * verifies the signature and then reads the subscription back from Razorpay;
 * nothing in this request moves the plan by itself.
 */
export function confirmRecurringCheckout(body: {
  razorpay_payment_id: string;
  razorpay_subscription_id: string;
  razorpay_signature: string;
}) {
  return apiRequest('/subscription/recurring/confirm', subscriptionSchema, {
    method: 'POST',
    body,
  });
}

/**
 * Tell the server Checkout finished a one-off plan payment, handing it what
 * Razorpay's handler returned. The server verifies the signature and reads
 * the payment back from Razorpay, so the plan moves without waiting for the
 * webhook; a failure here only means the webhook settles it instead.
 */
export function confirmPlanPayment(body: {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}) {
  return apiRequest('/subscription/payments/confirm', subscriptionSchema, {
    method: 'POST',
    body,
  });
}

/**
 * The referral discount this merchant holds, priced against every plan on sale.
 *
 * Its own endpoint rather than a block on the catalogue, which the public
 * pricing page also reads without a session: one URL whose body depends on who
 * is asking would be a caching hazard with two callers and two cache keys.
 *
 * `active` is false for the unreferred majority, and for a benefit that has
 * been revoked, spent or has expired - the response is the same shape either
 * way, so the screen has one branch instead of two.
 */
export const referralPlanPriceSchema = z.object({
  plan_code: z.string(),
  price_minor: z.number(),
  /** What comes off this plan's *first* charge. Zero when nothing applies. */
  discount_minor: z.number(),
  payable_minor: z.number(),
  /**
   * Why nothing comes off, when nothing does: `revoked`, `consumed`, `expired`,
   * `not_applicable`, `plan_not_covered`, `below_minimum`, or `free_plan` /
   * `trial_plan`, which mean there is no first charge to discount rather than
   * that the benefit is unusable.
   */
  reason: z.string().nullable().optional(),
  /**
   * What the discount would be worth if the merchant gave up this plan's free
   * trial for it - the only way to have it, since a trial's first charge is
   * collected by Razorpay from its own plan and is never ours to reduce. Set
   * only when `reason` is `trial_plan` and something would actually apply, so
   * the screen never offers a trade that costs the trial and returns nothing.
   */
  without_trial_minor: z.number().nullable().optional(),
});

export const merchantReferralSchema = z.object({
  active: z.boolean(),
  code: z.string().nullable().optional(),
  partner_name: z.string().nullable().optional(),
  /**
   * The same sentence the registration form showed - generated on the server
   * from the fields the discount is actually computed from, so the words and
   * the arithmetic cannot drift apart.
   */
  description: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  /** Null means unlimited while the benefit's own window is open. */
  remaining_cycles: z.number().nullable().optional(),
  expires_at: z.string().nullable().optional(),
  plans: z.array(referralPlanPriceSchema).optional().default([]),
});

export type MerchantReferral = z.infer<typeof merchantReferralSchema>;
export type ReferralPlanPrice = z.infer<typeof referralPlanPriceSchema>;

export function fetchMerchantReferral() {
  return apiRequest('/subscription/referral', merchantReferralSchema);
}

/**
 * Attach a promo or partner code at checkout, for a merchant who signed up
 * without one. Refused once a code is on the account - attribution is never
 * swapped - or once anything has been paid.
 */
export function applyMerchantReferral(code: string) {
  return apiRequest('/subscription/referral', merchantReferralSchema, {
    method: 'POST',
    body: { code },
  });
}

/** Stop auto-renewal. By default at the end of the paid period. */
export function cancelRecurringSubscription(atPeriodEnd = true) {
  return apiRequest('/subscription/recurring/cancel', subscriptionSchema, {
    method: 'POST',
    body: { at_period_end: atPeriodEnd },
  });
}

/** Re-read the mandate from Razorpay, for a page that looks stale. */
export function syncRecurringSubscription() {
  return apiRequest('/subscription/recurring/sync', subscriptionSchema, { method: 'POST' });
}

/** Undo a downgrade scheduled for the end of the cycle. */
export function keepCurrentPlan() {
  return apiRequest('/subscription/recurring/keep-plan', subscriptionSchema, { method: 'POST' });
}

export const subscriptionInvoiceSchema = z.object({
  id: z.string(),
  plan_code: z.string(),
  plan_name: z.string(),
  amount: z.string(),
  currency: z.string(),
  status: z.string(),
  period_start: z.string(),
  period_end: z.string(),
  paid_at: z.string().nullable(),
  failure_reason: z.string().nullable(),
  created_at: z.string(),
  provider: z.string(),
  provider_payment_id: z.string().nullable(),
  /** ONE_TIME, AUTHORIZATION (mandate set-up) or RECURRING (an automatic cycle). */
  kind: z.string().optional(),
  razorpay_subscription_id: z.string().nullable().optional(),
  /** The one-time setup add-on, when this charge carried it - its own line. */
  setup_fee_amount: z.string().nullable().optional(),
  /** A set-up waiting out a free trial: the day the first charge is due. */
  due_at: z.string().nullable().optional(),
  /** That set-up's setup add-on, which Razorpay took at authorisation. */
  setup_fee_paid: z.boolean().optional(),
  /** What that add-on cost with its GST. */
  setup_fee_paid_amount: z.string().nullable().optional(),
  /** The GST frozen on this charge; `"0"` on charges from before GST. */
  tax_rate: z.string().optional(),
  tax_amount: z.string().optional(),
  cgst_amount: z.string().optional(),
  sgst_amount: z.string().optional(),
  igst_amount: z.string().optional(),
  place_of_supply: z.string().nullable().optional(),
  /** The sequential tax-invoice number, `NC/2026-27/0001`, once paid. */
  invoice_number: z.string().nullable().optional(),
  /** Set when the download is a tax invoice rather than a receipt. */
  platform_gstin: z.string().nullable().optional(),
  /**
   * The credit for the unused part of the period this charge replaced,
   * already inside `amount`, with the days it was worked out from and the
   * credit note that documents it.
   */
  credit_amount: z.string().nullable().optional(),
  credit_days: z.number().nullable().optional(),
  credit_period_days: z.number().nullable().optional(),
  credit_note_id: z.string().nullable().optional(),
  credit_note_number: z.string().nullable().optional(),
  /** When Razorpay returned the money - the ₹5 mandate verification, mostly. */
  refunded_at: z.string().nullable().optional(),
  /** A pending set-up whose billing starts later: not unpaid, only not due yet. */
  scheduled: z.boolean().optional(),
});

export type SubscriptionInvoice = z.infer<typeof subscriptionInvoiceSchema>;

export const SUBSCRIPTION_INVOICE_STATUSES = [
  'PAID',
  'FAILED',
  'PENDING',
  'CANCELLED',
  'REFUNDED',
] as const;
export type SubscriptionInvoiceStatus = (typeof SUBSCRIPTION_INVOICE_STATUSES)[number];

/** ShopLaunch's charges to this merchant - not their invoices to shoppers. */
export function fetchSubscriptionInvoices(
  page = 1,
  { status, size }: { status?: SubscriptionInvoiceStatus; size?: number } = {},
) {
  const params = new URLSearchParams({ page: String(page) });
  if (size) params.set('size', String(size));
  if (status) params.set('status', status);
  return apiRequest(`/subscription/invoices?${params}`, pageSchema(subscriptionInvoiceSchema));
}

export const subscriptionInvoiceSummarySchema = z.object({
  currency: z.string(),
  paid_total: z.string(),
  counts: z.record(z.string(), z.number()),
  last_paid_at: z.string().nullable(),
  last_paid_amount: z.string().nullable(),
});

export type SubscriptionInvoiceSummary = z.infer<typeof subscriptionInvoiceSummarySchema>;

/** Totals over every charge, not just the page of history on screen. */
export function fetchSubscriptionInvoiceSummary() {
  return apiRequest('/subscription/invoices/summary', subscriptionInvoiceSummarySchema);
}

/**
 * Abandon a checkout that was started but not paid.
 *
 * Without this the merchant is stuck: the unpaid charge makes every later plan
 * change fail with `PAYMENT_IN_PROGRESS`, and only staff could clear it.
 */
export function cancelSubscriptionInvoice(invoiceId: string) {
  return apiRequest(`/subscription/invoices/${invoiceId}/cancel`, subscriptionInvoiceSchema, {
    method: 'POST',
  });
}

/**
 * The payment receipt for one of the shop's own paid charges, as a PDF.
 *
 * Fetched rather than linked: the endpoint wants the bearer token, which a
 * plain browser navigation would not carry.
 */
export function fetchSubscriptionReceiptPdf(invoiceId: string) {
  return apiRequestBlob(`/subscription/invoices/${invoiceId}/pdf`, {
    headers: { Accept: 'application/pdf' },
  });
}

/**
 * The credit note for the unused part of a period an upgrade cut short, as a
 * PDF. Fetched for the same reason the receipt is: the endpoint wants the
 * bearer token.
 */
export function fetchCreditNotePdf(creditNoteId: string) {
  return apiRequestBlob(`/subscription/credit-notes/${creditNoteId}/pdf`, {
    headers: { Accept: 'application/pdf' },
  });
}
